/* Keyboard accessibility for the Froala inline toolbar.
 *
 * Root cause: Froala renders toolbar buttons with tabindex="-1", which
 * prevents real DOM focus. Froala's blur handler only keeps the toolbar
 * visible when a child of .fr-toolbar has :focus — with tabindex="-1"
 * that check always fails and the toolbar closes as soon as the editor
 * blurs (e.g. on Tab or ArrowDown).
 *
 * Fix:
 *   1. Promote buttons to tabindex="0" while the toolbar is visible so
 *      Froala's blur guard finds :focus within .fr-toolbar and keeps it
 *      open.
 *   2. A document-level CAPTURE-phase listener intercepts Tab when the
 *      editor has focus. Capture fires before every element-level handler
 *      (including Froala's own queue), so e.preventDefault reliably stops
 *      the default focus-move before Froala can react.
 *   3. A second document capture listener handles arrow navigation and
 *      Escape while a toolbar button has focus.
 */

/* @ngInject */
export default function setupToolbarKeyboardAccessibility(
  getFroalaInstance: () => any,
): void {
  const getInlineToolbarEl = (): HTMLElement | null => {
    const inst = getFroalaInstance();
    return (inst.$tb as any)?.get(0) ?? null;
  };

  const TOOLBAR_BTN_SELECTOR = 'a.fr-command, button.fr-command, div.fr-command';

  const getInlineToolbarButtons = (): HTMLElement[] => {
    const toolbarEl = getInlineToolbarEl();
    if (!toolbarEl) return [];
    return Array.from(
      toolbarEl.querySelectorAll(TOOLBAR_BTN_SELECTOR) as NodeListOf<HTMLElement>,
    ).filter((btn) => (
      btn.offsetParent !== null
      && !btn.classList.contains('fr-disabled')
      // Froala hides closed dropdown panels with aria-hidden="true" (not
      // display:none), so offsetParent is still non-null for their items.
      // Exclude any button that lives inside an aria-hidden ancestor.
      && !btn.closest('[aria-hidden="true"]')
    ));
  };

  const setToolbarTabIndex = (tabIndexValue: string) => {
    const toolbarEl = getInlineToolbarEl();
    if (!toolbarEl) return;
    toolbarEl
      .querySelectorAll(TOOLBAR_BTN_SELECTOR)
      .forEach((btn) => btn.setAttribute('tabindex', tabIndexValue));
  };

  const isInlineToolbarVisible = (): boolean => {
    const toolbarEl = getInlineToolbarEl();
    if (!toolbarEl) return false;
    return window.getComputedStyle(toolbarEl).display !== 'none';
  };

  // Handles arrow / Tab / Enter / Space / Escape while focus is inside the toolbar.
  const handleToolbarKeydown = (e: KeyboardEvent) => {
    const toolbarEl = getInlineToolbarEl();
    if (!toolbarEl?.contains(document.activeElement)) return;

    const buttons = getInlineToolbarButtons();
    // Prefer an exact match; fall back to checking whether the focused
    // element is a child of a known button (e.g. a span/SVG inside the
    // font-style dropdown toggle <button>).
    let idx = buttons.indexOf(document.activeElement as HTMLElement);
    if (idx === -1) {
      idx = buttons.findIndex((btn) => btn.contains(document.activeElement));
    }
    if (idx === -1) return;

    switch (e.key) {
      case 'ArrowRight':
      case 'ArrowDown':
        e.preventDefault();
        e.stopImmediatePropagation();
        buttons[(idx + 1) % buttons.length]?.focus();
        break;
      case 'ArrowLeft':
      case 'ArrowUp':
        e.preventDefault();
        e.stopImmediatePropagation();
        buttons[(idx - 1 + buttons.length) % buttons.length]?.focus();
        break;
      case 'Tab': {
        // WAI-ARIA toolbar pattern: Tab/Shift+Tab exits the toolbar and
        // moves focus to the next/previous page element. Arrow keys handle
        // in-toolbar navigation instead.
        // Do NOT preventDefault — let the browser's native Tab advance
        // focus from the editor. Stop propagation so Froala's own handlers
        // don't interfere with the focus move.
        e.stopImmediatePropagation();
        const froala = getFroalaInstance();
        froala.events.enableBlur();
        froala.selection.restore();
        // Return focus to the editor first so the browser's Tab/Shift+Tab
        // moves relative to the editor's position in the tab order, not
        // the toolbar button's (toolbar buttons are outside the natural
        // tab order).
        froala.el.focus();
        break;
      }
      case 'Enter':
      case ' ': {
        // Froala toolbar items are <a>/<div> elements that don't receive
        // browser-native activation on Enter/Space — trigger it manually.
        //
        // Froala's bindClick binds TWO handlers:
        //   mousedown → marks button "fr-selected"
        //   mouseup   → if fr-dropdown: open/position dropdown panel
        //               else: call r() → commands.exec(cmd, params)
        //
        // Dropdown toggle buttons (fr-dropdown class) MUST go through the
        // full mousedown+mouseup sequence because the dropdown toggle logic
        // positions the panel using the button's DOM offset. Do NOT restore
        // the selection here — the fr-marker nodes must stay in the DOM so
        // they are available when the user later activates a dropdown item.
        //
        // Regular buttons restore the selection and call commands.exec()
        // directly (same as Froala's internal r()), then return focus to
        // the button if the toolbar is still visible.
        e.preventDefault();
        e.stopImmediatePropagation();
        const activeBtn = document.activeElement as HTMLElement;
        const froala = getFroalaInstance();
        if (activeBtn.classList.contains('fr-dropdown')) {
          const mouseOpts: MouseEventInit = { bubbles: true, cancelable: true, button: 0, view: window };
          activeBtn.dispatchEvent(new MouseEvent('mousedown', mouseOpts));
          activeBtn.dispatchEvent(new MouseEvent('mouseup', mouseOpts));
          // Do NOT call enableBlur() here: Froala's mouseup handler calls
          // disableBlur() internally when it opens the dropdown panel, so
          // calling enableBlur() synchronously after would undo that and cause
          // the toolbar to dismiss before the user can select a dropdown item.
          // The disableBlur/enableBlur pair is balanced via the toolbar.hide
          // event handler, which calls enableBlur whenever the toolbar hides.
        } else {
          const cmd = activeBtn.getAttribute('data-cmd');
          if (cmd) {
            const params: string[] = [];
            for (let i = 1; activeBtn.getAttribute(`data-param${i}`) !== null; i += 1) {
              params.push(activeBtn.getAttribute(`data-param${i}`) as string);
            }
            // Temporarily re-enable blur so the editor behaves normally
            // during command execution, then decide where focus lands.
            froala.events.enableBlur();
            froala.el.focus();
            froala.selection.restore();
            froala.commands.exec(cmd, params);
            // If the toolbar is still visible after the command (e.g. a
            // formatting toggle on an active selection), return focus to
            // the button so arrow-key navigation can continue.
            if (isInlineToolbarVisible()) {
              froala.events.disableBlur();
              activeBtn.focus();
            }
          }
        }
        break;
      }
      case 'Escape': {
        e.preventDefault();
        e.stopImmediatePropagation();
        const froala = getFroalaInstance();
        froala.events.enableBlur();
        froala.selection.restore();
        froala.el.focus();
        // Froala may re-show the toolbar immediately after el.focus() because
        // the selection is still active. Hide it explicitly so a single Escape
        // closes the toolbar rather than requiring a second press.
        froala.toolbar.hide();
        break;
      }
      default:
        break;
    }
  };

  // Intercepts Tab from the editor and routes focus into the toolbar. Runs in
  // capture phase so it fires before Froala's own handlers. Only Tab is used
  // to enter the toolbar, following the standard WAI-ARIA toolbar pattern.
  const handleEditorTabIntoToolbar = (e: KeyboardEvent) => {
    if (e.key !== 'Tab') return;
    const froala = getFroalaInstance();
    if (!froala.el.contains(document.activeElement)) return;
    if (!isInlineToolbarVisible()) return;

    const buttons = getInlineToolbarButtons();
    if (!buttons.length) return;

    e.preventDefault();
    e.stopImmediatePropagation();
    setToolbarTabIndex('0');
    froala.events.disableBlur();
    froala.selection.save();
    buttons[0].focus();
  };

  const instance = getFroalaInstance();

  if (instance.__keyboardAccessibilitySetup) return;
  instance.__keyboardAccessibilitySetup = true;

  instance.events.on('toolbar.show', () => {
    setToolbarTabIndex('0');
    document.removeEventListener('keydown', handleToolbarKeydown, true);
    document.addEventListener('keydown', handleToolbarKeydown, true);
  });

  instance.events.on('toolbar.hide', () => {
    setToolbarTabIndex('-1');
    document.removeEventListener('keydown', handleToolbarKeydown, true);
    getFroalaInstance().events.enableBlur();
  });

  document.addEventListener('keydown', handleEditorTabIntoToolbar, true);

  // Clean up native DOM listeners when this Froala instance is destroyed.
  instance.events.on('destroy', () => {
    document.removeEventListener('keydown', handleEditorTabIntoToolbar, true);
    document.removeEventListener('keydown', handleToolbarKeydown, true);
  });
}
